import { z } from "zod";

const optional = () => z.string().optional().or(z.literal(""));

export const vendorFormSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(200),
  code: z.string().trim().min(1, "Code is required").max(20),
  vendor_type: z.enum(["coil", "sheet", "pipe", "scrap", "services", "other"]),
  gst_treatment: z.enum([
    "registered",
    "unregistered",
    "composition",
    "sez",
    "export",
    "deemed_export",
  ]),
  gstin: optional(),
  pan_no: optional(),
  work_phone: optional(),
  email: z.string().email("Invalid email").optional().or(z.literal("")),
  website: optional(),
  addr1: z.string().trim().min(1, "Address is required"),
  addr2: optional(),
  city: z.string().trim().min(1, "City is required"),
  state: z.string().trim().min(1, "State is required"),
  pincode: z.string().trim().min(1, "PIN is required"),
  place_of_supply: optional(),
  payment_terms: z.enum([
    "advance",
    "against_delivery",
    "net_15",
    "net_30",
    "net_45",
    "net_60",
    "net_90",
    "letter_of_credit",
  ]),
  credit_days: z.coerce.number().int().min(0).max(365),
  credit_limit: z.coerce.number().min(0),
  batch_prefix: z.string().trim().min(1, "Batch prefix is required").max(10),
  notes: optional(),
  opening_balance: z.coerce.number(),
  opening_balance_as_of: optional(),
  is_active: z.boolean(),
}).superRefine((values, ctx) => {
  if (values.opening_balance !== 0 && !values.opening_balance_as_of?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["opening_balance_as_of"],
      message: "Set the as-of date for opening balance",
    });
  }
});

export type VendorFormValues = z.infer<typeof vendorFormSchema>;

export const emptyVendorForm: VendorFormValues = {
  name: "",
  code: "",
  vendor_type: "coil",
  gst_treatment: "registered",
  gstin: "",
  pan_no: "",
  work_phone: "",
  email: "",
  website: "",
  addr1: "",
  addr2: "",
  city: "",
  state: "",
  pincode: "",
  place_of_supply: "",
  payment_terms: "net_30",
  credit_days: 30,
  credit_limit: 0,
  batch_prefix: "VEN",
  notes: "",
  opening_balance: 0,
  opening_balance_as_of: "",
  is_active: true,
};

export const contactFormSchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  designation: optional(),
  department: optional(),
  mobile: optional(),
  alt_mobile: optional(),
  email: z.string().email().optional().or(z.literal("")),
  is_primary: z.boolean(),
  is_active: z.boolean(),
});

export type ContactFormValues = z.infer<typeof contactFormSchema>;

export const emptyContactForm: ContactFormValues = {
  name: "",
  designation: "",
  department: "",
  mobile: "",
  alt_mobile: "",
  email: "",
  is_primary: false,
  is_active: true,
};

export const addressFormSchema = z.object({
  title: z.string().trim().min(1),
  addr1: z.string().trim().min(1),
  addr2: optional(),
  city: z.string().trim().min(1),
  state: z.string().trim().min(1),
  pincode: z.string().trim().min(1),
  address_type: z.enum(["billing", "shipping", "other"]),
  is_default: z.boolean(),
  notes: optional(),
});

export type AddressFormValues = z.infer<typeof addressFormSchema>;

export const emptyAddressForm: AddressFormValues = {
  title: "Address",
  addr1: "",
  addr2: "",
  city: "",
  state: "",
  pincode: "",
  address_type: "billing",
  is_default: false,
  notes: "",
};
